import React, { useEffect, useMemo, useState } from "react";
import { FaStar, FaTrophy, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import axiosInstance from "../config/axios";
import { formatStatMoney } from "../utils/dashboardStatsUtils";
import {
  currentMonthCursor,
  monthTitle,
  shiftMonth,
} from "../utils/dashboardMonthlyChartUtils";
import {
  performerValue,
  topPerformers,
} from "../utils/dashboardPerformerUtils";

function formatValue(row, metric) {
  if (metric === "trades") return `${row.trades} trade${row.trades === 1 ? "" : "s"}`;
  return formatStatMoney(row.commission);
}

function PerformerBars({ rows, metric, barClass }) {
  const max = Math.max(...rows.map((row) => performerValue(row, metric)), 1);
  return (
    <ul className="space-y-2.5">
      {rows.map((row, index) => {
        const value = performerValue(row, metric);
        const width = Math.max(6, (value / max) * 100);
        return (
          <li key={row.id}>
            <div className="flex items-center justify-between gap-2 text-xs mb-1">
              <span className="font-medium text-gray-800 truncate">
                <span className="text-gray-400 mr-1">{index + 1}.</span>
                {row.name}
              </span>
              <span className="text-gray-600 flex-shrink-0">
                {formatValue(row, metric)}
              </span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${barClass}`}
                style={{ width: `${width}%` }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function PerformerCard({
  title,
  Icon,
  iconWrap,
  barClass,
  rows,
  metric,
  loading,
  monthLabel,
  onPrev,
  onNext,
  disableNext,
  panelClass = "bg-white border-gray-100",
}) {
  return (
    <div className={`rounded-lg shadow-sm border p-3 ${panelClass}`}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <div
            className={`w-7 h-7 rounded-md ${iconWrap} text-white flex items-center justify-center flex-shrink-0`}
          >
            <Icon className="text-xs" />
          </div>
          <h3 className="text-sm font-semibold text-blue-900 truncate">{title}</h3>
        </div>
        {monthLabel ? (
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={onPrev}
              className="p-1 rounded hover:bg-gray-100 text-gray-500"
              aria-label="Previous month"
            >
              <FaChevronLeft className="text-[10px]" />
            </button>
            <span className="text-xs text-gray-600 min-w-[7.5rem] text-center">
              {monthLabel}
            </span>
            <button
              type="button"
              onClick={onNext}
              disabled={disableNext}
              className="p-1 rounded hover:bg-gray-100 text-gray-500 disabled:opacity-30"
              aria-label="Next month"
            >
              <FaChevronRight className="text-[10px]" />
            </button>
          </div>
        ) : (
          <span className="text-xs text-gray-500 flex-shrink-0">
            {new Date().getFullYear()}
          </span>
        )}
      </div>
      {loading ? (
        <p className="h-44 flex items-center justify-center text-sm text-gray-400">
          Loading…
        </p>
      ) : rows.length === 0 ? (
        <p className="h-44 flex items-center justify-center text-sm text-gray-400 text-center px-4">
          No closed trades for this period.
        </p>
      ) : (
        <PerformerBars rows={rows} metric={metric} barClass={barClass} />
      )}
    </div>
  );
}

const DashboardPerformers = () => {
  const thisMonth = useMemo(() => currentMonthCursor(), []);
  const [cursor, setCursor] = useState(thisMonth);
  const [metric, setMetric] = useState("commission");
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);

  const disableNext =
    cursor.year > thisMonth.year ||
    (cursor.year === thisMonth.year && cursor.month >= thisMonth.month);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axiosInstance.get("/trades");
        if (!cancelled) setTrades(res.data || []);
      } catch (err) {
        console.error("Error loading dashboard performers:", err);
        if (!cancelled) setTrades([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const ytdRows = useMemo(() => {
    const year = new Date().getFullYear();
    return topPerformers(trades, { year }, metric);
  }, [trades, metric]);

  const monthRows = useMemo(
    () =>
      topPerformers(
        trades,
        { year: cursor.year, month: cursor.month },
        metric,
      ),
    [trades, cursor, metric],
  );

  return (
    <div className="px-6 pb-6">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h2 className="text-sm font-semibold text-blue-900">
          Agent performance
        </h2>
        <label className="flex items-center gap-2 text-xs text-gray-600">
          Rank by
          <select
            value={metric}
            onChange={(e) => setMetric(e.target.value)}
            className="border border-gray-200 rounded-md px-2 py-1 text-xs text-gray-800 bg-white"
          >
            <option value="commission">Commission</option>
            <option value="trades">Trades</option>
          </select>
        </label>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <PerformerCard
          title="Top Performer (YTD)"
          Icon={FaStar}
          iconWrap="bg-amber-500"
          barClass="bg-amber-500"
          rows={ytdRows}
          metric={metric}
          loading={loading}
          panelClass="bg-amber-50/80 border-amber-100"
        />
        <PerformerCard
          title="Monthly Performers"
          Icon={FaTrophy}
          iconWrap="bg-emerald-500"
          barClass="bg-emerald-500"
          rows={monthRows}
          metric={metric}
          loading={loading}
          monthLabel={monthTitle(cursor)}
          onPrev={() => setCursor((c) => shiftMonth(c, -1))}
          onNext={() => setCursor((c) => shiftMonth(c, 1))}
          disableNext={disableNext}
          panelClass="bg-emerald-50/80 border-emerald-100"
        />
      </div>
    </div>
  );
};

export default DashboardPerformers;
